import {
  getPlans,
  getTierPlans,
  getSelectedTierPlan,
  getAllTierPlans,
  getSelectedPlan,
  getCurrentSubscription,
  getCurrentTierPlan,
  getAllSubscriptions,
  hasOnlyFreePlans,
  isCryptoPaymentEnabled,
  hasRecurringPayment,
  isRecurringPaymentMethod,
} from '@/composables/paymentPlanMethods'
import { seg_track, seg_getTotalAndCurrency, seg_getProduct } from '@/helpers/segment'

const payment_mix = {
  methods: {
    segTrack() {
      const { plan } = this.selectedTierPlan
      const item = {
        plan,
        price: { price: plan?.price },
      }
      seg_track('checkout_step_viewed', {
        step: 2,
        checkout_type: 'Purchase Funnel',
        // currency: 'USD',
        ...seg_getTotalAndCurrency([item]),
        products: [
          {
            ...seg_getProduct(item),
            // quantity: 1,
          },
        ],
      })
    },
    switchToYearly() {
      const yearlyPlan = this.selectedTierPlans.find(i => i.plan.interval === 'YEARLY')
      const { plan } = yearlyPlan
      this.$router.replace({
        query: {
          ...this.$route.query,
          gm_type: plan.type.toLowerCase(),
          gm_tier: plan.tier.toLowerCase(),
          gm_interval: plan.interval.toLowerCase(),
        },
      })
      seg_track()
    },
    /**
     * If we don't have a previous route, meaning this page is the first page,
     * or the previous page was outside of the app,
     * we go to the UserSubscription page.
     * Otherwise, we go back to the previous page.
     */
    goBack() {
      const pathName = this.$route.name
      const previousRoute = decodeURIComponent(sessionStorage.getItem('previousRoute') ?? '')

      // stripe somehow manages to insert checkout-page 2x into history
      // if checkout results in error - user then needs to click backBtn 2 times
      setTimeout(() => {
        if (this.$route.name === pathName) {
          this.goBack()
        }
      }, 120)

      return previousRoute
        ? this.$router.go(-1)
        : this.$router.replace({
            name: this.$store.getters.user_has_flag('REACT_SUBSCRIPTION_OVERVIEW')
              ? 'UserSubscriptionsOverview'
              : 'UserSubscription',
          })
    },
  },
  computed: {
    gmTier() {
      return this.currentPlan.tier
    },
    gm_type() {
      return this.$route.query.gm_type
    },
    gm_tier() {
      return this.$route.query.gm_tier
    },
    gm_interval() {
      return this.$route.query.gm_interval
    },
    gmStraddle() {
      return this.gm_type === 'straddle_ante_cash'
    },

    anyCheapPlan() {
      if (this.gmStraddle) {
        return this.plans.cash.premium.monthly.cheap
      }
      return this.plans.cash.starter.monthly.cheap
    },
    selectedTierPlan() {
      return getSelectedTierPlan({
        type: this.gm_type,
        tier: this.gm_tier,
        interval: this.gm_interval,
        selectedTierPlans: this.selectedTierPlans,
      })
    },
    selectedTierPlans() {
      return getTierPlans({ type: this.gm_type, tier: this.gm_tier })
    },
    allTierPlans() {
      return getAllTierPlans(this.gm_type)
    },
    selectedPlan() {
      return getSelectedPlan({
        type: this.gm_type,
        tier: this.gm_tier,
        interval: this.gm_interval,
        plans: this.plans,
      })
    },
    allSubs() {
      return getAllSubscriptions()
    },
    currentSub() {
      return getCurrentSubscription(this.gm_type)
    },
    currentPlan() {
      return this.currentSub?.plan
    },
    currentTierPlan() {
      return getCurrentTierPlan(this.currentPlan, this.allTierPlans)
    },
    isCurrentTierPlanFree() {
      return (
        this.currentTierPlan?.plan.tier === 'FREE' || this.currentTierPlan?.plan.tier === 'TRIAL'
      )
    },
    plans() {
      return getPlans()
    },
    hasOnlyFreePlans() {
      return hasOnlyFreePlans(this.allSubs)
    },
    alipayEnabled() {
      return this.$store.getters.user_has_flag('ALIPAY')
    },
    wechatPayEnabled() {
      return this.$store.getters.user_has_flag('WECHAT_PAY')
    },
    /** Crypto is enabled:
     * - FF CONFIRMO: purchasing first subscription of a gameformat (not a upgrade)
     * - FF CONFIRMO_UPGRADES: upgrading confirmo provided subscriptions (excluding other providers)
     * TODO: enable all upgrades when BE can cancel existing non-confirmo subscription
     */
    cryptoPaymentEnabled() {
      return this.currentPlan && isCryptoPaymentEnabled(this.currentPlan)
    },
    cryptoSubscriptionActive() {
      return this.currentSub?.plan?.provider === 'Confirmo'
    },
    paypalEnabled() {
      return this.$store.getters.user_has_flag('GATESBTNS')
    },
    adyenEnabled() {
      return (
        this.$store.getters.user_has_flag('ADYEN') &&
        (this.hasOnlyFreePlans || this.currentPlan.provider !== 'Stripe')
      )
    },
    cardEnabled() {
      return this.hasOnlyFreePlans || !this.currentPlanProviderIsGtowizard // disabled untill BE has cancelling Gtowizard subscriptions
    },

    /**
     * Checks if the current plan is a Gtowizard plan
     * The this.currentPlan returns fallback to the first subscription if the current plan is not found, so we need to check if the current plan is valid for the current game format
     */
    currentPlanProviderIsGtowizard() {
      if (this.gm_type !== this.currentPlan?.type) {
        return false
      }

      return this.currentPlan.provider === 'Gtowizard'
    },

    /**
     * Returns an array of allowed gates
     * @returns Gate type (card, paypal, crypto, other)
     */
    allowedPaymentMethods() {
      const result = this.cardEnabled ? ['card'] : []

      if (this.cryptoPaymentEnabled) {
        result.push('crypto')
      }
      if (this.alipayEnabled || this.wechatPayEnabled) {
        result.push('other')
      }
      if (this.paypalEnabled) {
        result.push('paypal')
      }

      return result
    },
    multiplePaymentMethods() {
      return this.allowedPaymentMethods.length > 1
    },
    isCurrentPlanRecurring() {
      return (
        hasRecurringPayment(this.currentPlan) &&
        isRecurringPaymentMethod(this.currentSub.latest_payment_method)
      )
    },
  },
}

export default payment_mix
